import { useAnalyticsContext } from '@prezly/analytics-nextjs';
import type { ExtendedStory } from '@prezly/sdk';
import { DocumentNode, ImageNode } from '@prezly/story-content-format';
import type { Node } from '@prezly/story-content-format';
import { getLocalizedCategoryData, isEmbargoStory } from '@prezly/theme-kit-core';
import { StorySeo, useCurrentLocale } from '@prezly/theme-kit-nextjs';
import Image from '@prezly/uploadcare-image';
import classNames from 'classnames';
import dynamic from 'next/dynamic';

import { StoryLinks, StoryPublicationDate } from '@/components';
import { useDevice, useThemeSettings } from '@/hooks';
import { getStoryImageSizes, PUBLICITY_ASSETS_CATEGORY_SLUG } from '@/utils';

import Layout from '../Layout';

import { HeaderRenderer } from './HeaderRenderer';

import styles from './Story.module.scss';

const CategoriesList = dynamic(() => import('@/components/CategoriesList'));
const ContentRenderer = dynamic(() => import('@/components/ContentRenderer'));
const Embargo = dynamic(() => import('./Embargo'));

type Props = {
    story: ExtendedStory;
};

function getFilteredNodes(nodes: Node | Node[]): Node | Node[] {
    if (DocumentNode.isDocumentNode(nodes) && ImageNode.isImageNode(nodes.children[0])) {
        return {
            ...nodes,
            children: nodes.children.slice(1),
        };
    }
    return nodes;
}

function Story({ story }: Props) {
    const { showDate } = useThemeSettings();
    const { isTablet } = useDevice();
    const { isEnabled: isAnalyticsEnabled } = useAnalyticsContext();
    const currentLocale = useCurrentLocale();

    if (!story) {
        return null;
    }

    const { content, categories, links, visibility } = story;
    const nodes = JSON.parse(content);
    const headerImage = story.header_image ? JSON.parse(story.header_image) : null;
    const hasHeaderImage = Boolean(headerImage);
    const hasCategories = categories.length > 0;
    const filteredNodes = getFilteredNodes(nodes);

    const url = links.short || links.newsroom_view;

    const isPublicityAssetStory = categories.some(
        (category) =>
            getLocalizedCategoryData(category, currentLocale).slug ===
            PUBLICITY_ASSETS_CATEGORY_SLUG,
    );

    return (
        <Layout>
            <StorySeo story={story} noindex={!isAnalyticsEnabled} />
            <article className={styles.story}>
                <div
                    className={classNames(styles.container, {
                        [styles.withImage]: hasHeaderImage,
                    })}
                >
                    {headerImage && (
                        <Image
                            alt=""
                            className={styles.mainImage}
                            objectFit="cover"
                            layout="fill"
                            imageDetails={headerImage}
                            sizes={getStoryImageSizes()}
                        />
                    )}
                    {isEmbargoStory(story) && <Embargo story={story} />}

                    <HeaderRenderer nodes={filteredNodes} />

                    {isTablet && url && visibility === 'public' && <StoryLinks url={url} />}
                    <ContentRenderer nodes={filteredNodes} story={story} />

                    {showDate && !isPublicityAssetStory && (
                        <p className={styles.date}>
                            <StoryPublicationDate story={story} />
                        </p>
                    )}
                    {hasCategories && (
                        <div className={styles.categories}>
                            <CategoriesList
                                categories={categories}
                                showAllCategories
                                linkClassName={styles.categoryLink}
                            />
                        </div>
                    )}
                </div>
            </article>
            {!isTablet && url && visibility === 'public' && <StoryLinks url={url} />}
        </Layout>
    );
}

export default Story;
